import { useEffect, useRef, useState } from 'react';
import type { CSSProperties, JSX, ReactNode } from 'react';
import type { AnnouncementPosition } from '@chirpback/protocol';
import type { HostBridge } from '../../../../host.js';
import type { Palette } from '../../../../_theme/palette.js';
import { bannerAnim } from '../../../../motion/keyframes.js';

interface BannerShellProps {
  palette: Palette;
  bridge: HostBridge;
  position?: AnnouncementPosition;
  children: ReactNode;
}

export function BannerShell({ palette, bridge, position, children }: BannerShellProps): JSX.Element {
  const side = position === 'bottom' ? 'bottom' : 'top';
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [measuredHeight, setMeasuredHeight] = useState<number | undefined>();

  useEffect(() => {
    const element = contentRef.current;
    if (!element) return;
    const observer = new ResizeObserver((entries) => {
      const height = Math.ceil(entries[0]?.contentRect.height ?? 0);
      if (height > 0) setMeasuredHeight(height);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    bridge.send('chirp:surface:placement', {
      layout: 'banner',
      position: side,
      height: measuredHeight,
    });
  }, [bridge, side, measuredHeight]);

  return (
    <div style={wrapperStyle(palette, side)}>
      <div ref={contentRef} style={innerStyle(palette)}>
        {children}
      </div>
    </div>
  );
}

function wrapperStyle(palette: Palette, side: 'top' | 'bottom'): CSSProperties {
  // Dark themes get a stronger shadow; light themes get a hairline border
  // plus a softer drop. Either way the strip reads as a deliberate surface
  // floating over the host page, not a stray notification.
  const isDarkBg = isLightOnDark(palette);
  return {
    position: 'fixed',
    left: 0,
    right: 0,
    [side]: 0,
    background: palette.background,
    color: palette.text,
    fontFamily: palette.font,
    boxShadow: isDarkBg
      ? side === 'top'
        ? '0 8px 28px rgba(0,0,0,0.28)'
        : '0 -8px 28px rgba(0,0,0,0.28)'
      : side === 'top'
        ? '0 6px 20px rgba(15,23,42,0.06)'
        : '0 -6px 20px rgba(15,23,42,0.06)',
    borderBottom: side === 'top' && !isDarkBg ? `1px solid ${palette.border}` : 'none',
    borderTop: side === 'bottom' && !isDarkBg ? `1px solid ${palette.border}` : 'none',
    animation: bannerAnim(side),
    // Sit above just about everything. Host pages can still cover it with
    // higher z-index by design — this is a non-modal informational strip.
    zIndex: 2147483600,
  };
}

function innerStyle(palette: Palette): CSSProperties {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    // 86 % rail with 7 % gutters on each side. Keeps the badge / title on
    // the far left and the CTA / close on the far right at every viewport
    // width; max-width caps it on ultrawide so the text doesn't read as
    // one absurdly long line on 4K monitors.
    width: '86%',
    maxWidth: 1800,
    margin: '0 auto',
    color: palette.text,
  };
}

function isLightOnDark(palette: Palette): boolean {
  const hex = (palette.background || '').replace('#', '');
  if (hex.length !== 6 && hex.length !== 3) return false;
  const full = hex.length === 3 ? hex.split('').map((c) => c + c).join('') : hex;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance < 0.5;
}
